import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CRM_SUBMODULES, hasCrmSubAccess, CrmSubmodule } from '../../../utils/constants';

interface StitchLayoutProps {
    children: React.ReactNode;
}

const StitchLayout: React.FC<StitchLayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isDarkMode, setIsDarkMode] = useState(false);
    const roles: string[] | undefined = useSelector((s: any) => s.authStore?.user?.roles);

    const allMenuItems: { name: string; icon: string; path: string; permission: CrmSubmodule }[] = [
        { name: 'Freshdesk Dashboard', icon: '📊', path: '/crm/dashboard', permission: CRM_SUBMODULES.DASHBOARD },
        { name: 'Ozonetel Dashboard', icon: '🎛️', path: '/crm/ozonetel-dashboard', permission: CRM_SUBMODULES.OZONETEL_DASHBOARD },
        { name: 'Agent Performance', icon: '📈', path: '/crm/agent-performance', permission: CRM_SUBMODULES.AGENT_PERFORMANCE },
        { name: 'Escalations', icon: '🚨', path: '/crm/escalations', permission: CRM_SUBMODULES.ESCALATIONS },
        { name: 'Call Track', icon: '📞', path: '/crm/ozonetel-call-track', permission: CRM_SUBMODULES.CALL_TRACK },
        { name: 'Customers', icon: '👥', path: '/crm/search', permission: CRM_SUBMODULES.CUSTOMERS },
        { name: 'Freshdesk Tickets', icon: '📨', path: '/crm/freshdesk-tickets', permission: CRM_SUBMODULES.FRESHDESK_TICKETS },
        { name: 'Interaction Timeline', icon: '🕒', path: '/crm/interaction-timeline', permission: CRM_SUBMODULES.INTERACTION_TIMELINE },
        // { name: 'Sahara Voice AI', icon: '🤖', path: '/crm/sahara-voice-ai', permission: CRM_SUBMODULES.CALL_TRACK },
        { name: 'Samaira RFQ Calls', icon: '🤙', path: '/crm/samaira-rfq-calls', permission: CRM_SUBMODULES.SAMAIRA_RFQ_CALLS },
        { name: 'AI Call Simulator', icon: '📡', path: '/crm/ai-call-simulator', permission: CRM_SUBMODULES.AI_CALL_SIMULATOR },
        { name: 'Dev Toolkit Trace', icon: '🛠️', path: '/crm/developer-toolkit-trace', permission: CRM_SUBMODULES.DEV_TOOLKIT_TRACE },
    ];

    // Only show the sub-menus the logged-in user has a CRM_* role for.
    const menuItems = allMenuItems.filter((item) => hasCrmSubAccess(roles, item.permission));

    const isMenuItemActive = (item: any) => {
        if (item.path === location.pathname) return true;

        if (item.name === 'Customers') {
            return location.pathname.includes('/crm/search') || location.pathname.includes('/crm/customer/');
        }
        return false;
    };

    return (
        <div className={`min-h-screen flex ${isDarkMode ? 'dark bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
            {/* Sidebar */}
            <div className={`w-64 flex-shrink-0 flex flex-col border-r ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                {/* Logo Area */}
                <div className={`h-16 flex items-center px-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                            M
                        </div>
                        <span className={`font-bold text-xl tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Moglix CRM</span>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                    {menuItems.map((item) => {
                        const isActive = isMenuItemActive(item);
                        return (
                            <button
                                key={item.name}
                                onClick={() => navigate(item.path)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive
                                    ? 'bg-blue-50 text-blue-600'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                            >
                                <span className="text-lg">{item.icon}</span>
                                {item.name}
                            </button>
                        );
                    })}
                </nav>

                {/* User Profile Footer */}
                {/* <div className="p-4 border-t border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                            RS
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">Rahul Sharma</p>
                        </div>
                    </div>
                </div> */}
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header */}
                <header className={`h-16 flex items-center justify-between px-8 border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-center gap-4">
                        {/* Breadcrumbs or Page Title could go here */}
                        <div className="flex items-center gap-2 text-sm">
                            <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Moglix</span>
                            <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>/</span>
                            <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Customer Journey CRM</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Theme toggle - commented out for later release */}
                        {/* <button
                            onClick={() => setIsDarkMode(!isDarkMode)}
                            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                        >
                            {isDarkMode ? '☀️' : '🌙'}
                        </button> */}
                    </div>
                </header>

                {/* Content Area */}
                <main className={`flex-1 overflow-auto p-8 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                    {children}
                </main>
            </div>
        </div>
    );
};

export default StitchLayout;
